import { trackProductEvent } from "../../../lib/product-analytics";
import { useRef, useState } from "react";
import { Platform } from "react-native";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  useTailoredCVs,
  useDeleteTailoredCV,
} from "../../../hooks/useTailoredCvApi";
import { useAuth } from "../../../hooks/useAuth";
import { useToast } from "../../../hooks/useToast";
import { useLocale } from "../../../hooks/useLocale";
import { useUaePassStatus } from "../../../hooks/useUaePassStatus";
import { useSettingsDetail } from "../../../hooks/useSettingsDetail";
import { confirm } from "../../../lib/confirm-action";
import { clearMemory } from "../../../lib/agent-threads";
import { openAccountGuide } from "../../../lib/marketing-links";
import { openUaePassFlow } from "../../../lib/uae-pass-navigation";
import { SettingsAccountDeletion } from "./SettingsAccountDeletion";
import { UaePassPanel } from "./UaePassPanel";
import { SettingsDetailSurface } from "./SettingsDetailSurface";
import { DataManagementMainCvSheet } from "./DataManagementCvSectionSheet";
import { isSettingsCategoryAvailable } from "../settings-categories";
import {
  ControlStack,
  ControlHeading,
  ControlCopy,
  ControlSection,
  ControlRow,
  ControlAction,
  ControlHelp,
  ControlQueryState,
} from "./DataControlParts";
type Detail =
  | { kind: "uaePass" | "connections" | "memory" }
  | { kind: "cv"; id: string; title: string; autoMatch: boolean };
export function DataManagementPanel({
  onNavigate,
  onDeleteAccount,
}: {
  onNavigate?: (href: string) => void;
  onDeleteAccount?: () => void;
}) {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const { locale } = useLocale();
  const router = useRouter();
  const toast = useToast();
  const queryClient = useQueryClient();
  const cvs = useTailoredCVs(!!user);
  const uaePass = useUaePassStatus(!!user);
  const deleteCv = useDeleteTailoredCV();
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mainCvOpen, setMainCvOpen] = useState(false);
  const lock = useRef(false);
  // Placeholder kind: the surface stays closed until the first open() call.
  const surface = useSettingsDetail<Detail>({ kind: "memory" });
  const navigate = (href: string) => {
    if (onNavigate) return onNavigate(href);
    if (Platform.OS === "ios" && !href.startsWith("/settings/")) {
      router.replace(href as never);
      return;
    }
    router.push(href as never);
  };
  const leave = (href: string) => surface.leave(() => navigate(href));
  const perform = async (
    kind: "cv" | "memory",
    id?: string,
    title?: string,
  ) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      if (
        !(await confirm({
          title: t(
            kind === "cv"
              ? "settings.dataManagement.deleteCvTitle"
              : "settings.dataManagement.clearMemoryTitle",
            { title },
          ),
          message: t(
            kind === "cv"
              ? "settings.dataManagement.deleteCvBody"
              : "settings.dataManagement.clearMemoryBody",
            { title },
          ),
          confirmLabel: t(
            kind === "cv"
              ? "settings.dataManagement.deleteCv"
              : "settings.dataManagement.clearMemory",
          ),
          cancelLabel: t("actions.cancel"),
          destructive: true,
        }))
      )
        return;
      if (kind === "cv" && id) {
        await deleteCv.mutateAsync(id);
        // The strongest regret signal in the product.
        trackProductEvent("cv_deleted", { cv_kind: "tailored", source: "settings" });
        await cvs.refetch();
      } else if (kind === "memory") {
        await clearMemory();
        await queryClient.invalidateQueries({ queryKey: ["agent", "memory"] });
      }
      toast.success(t("settings.dataManagement.removed"));
      surface.close();
    } catch {
      toast.error(t("settings.dataManagement.actionFailed"));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const titles = {
    uaePass: t("settings.uaePass.title"),
    connections: t("settings.dataManagement.connectionsTitle"),
    memory: t("settings.dataManagement.memoryTitle"),
  };
  const detail = surface.detail;
  const title = detail.kind === "cv" ? detail.title : titles[detail.kind];
  const footer =
    detail.kind === "cv" ? (
      <ControlAction
        title={t("settings.dataManagement.deleteCv")}
        disabled={busy}
        primary
        onPress={() => void perform("cv", detail.id, detail.title)}
      />
    ) : detail.kind === "memory" ? (
      <ControlAction
        title={t("settings.dataManagement.clearMemory")}
        disabled={busy}
        primary
        onPress={() => void perform("memory")}
      />
    ) : undefined;
  return (
    <>
      <ControlStack gap={24}>
        <ControlHeading
          title={t("settings.dataManagement.title")}
          description={t("settings.dataManagement.description")}
        />
        <ControlSection title={t("settings.controls.cvData")}>
          <ControlRow
            icon="profile"
            title={t("settings.cvPrivacy.mainCv")}
            description={t("settings.controls.mainDataHint")}
            onPress={() => setMainCvOpen(true)}
          />
          <ControlQueryState
            loading={cvs.isPending}
            error={cvs.isError}
            retry={cvs.refetch}
          />
          {cvs.isSuccess && !cvs.data.length ? (
            <ControlCopy>{t("settings.cvPrivacy.noCvs")}</ControlCopy>
          ) : null}
          {cvs.isSuccess
            ? cvs.data.map((cv) => (
                <ControlRow
                  key={cv.id}
                  icon="cv"
                  title={cv.title}
                  description={t("settings.controls.savedCvHint")}
                  onPress={() =>
                    surface.open({
                      kind: "cv",
                      id: cv.id,
                      title: cv.title,
                      autoMatch: !!cv.isDefaultAutoApplyCv,
                    })
                  }
                />
              ))
            : null}
        </ControlSection>
        <ControlSection title={t("settings.controls.identityConnections")}>
          <ControlRow
            icon="privacy"
            title={t("settings.uaePass.title")}
            description={t("settings.controls.uaePassHint")}
            value={t(
              uaePass.isPending
                ? "labels.loading"
                : uaePass.isError
                  ? "settings.controls.unavailable"
                  : uaePass.data?.isLinked
                    ? "settings.uaePass.statusLinked"
                    : "settings.uaePass.statusUnlinked",
            )}
            onPress={() => surface.open({ kind: "uaePass" })}
          />
          <ControlRow
            icon="connection"
            title={t("settings.dataManagement.connectionsTitle")}
            description={t("settings.controls.connectionsHint")}
            onPress={() => surface.open({ kind: "connections" })}
          />
        </ControlSection>
        <ControlSection title={t("settings.controls.memoryAccount")}>
          <ControlRow
            icon="memory"
            title={t("settings.dataManagement.memoryTitle")}
            description={t("settings.controls.memoryHint")}
            onPress={() => surface.open({ kind: "memory" })}
          />
          <ControlRow
            icon="account"
            title={t("settings.dataManagement.accountTitle")}
            description={t("settings.controls.accountHint")}
            onPress={() =>
              onDeleteAccount ? onDeleteAccount() : setDeletingAccount(true)
            }
          />
        </ControlSection>
        <ControlHelp
          title={t("settings.dataManagement.guide")}
          onPress={() => void openAccountGuide("data-management", locale)}
        />
      </ControlStack>
      <SettingsDetailSurface
        visible={surface.visible}
        title={title}
        onClose={surface.close}
        onAfterClose={surface.afterClose}
        footer={footer}
      >
        {detail.kind === "cv" ? (
          <>
            <ControlCopy>
              {t("settings.dataManagement.deleteCvBody", {
                title: detail.title,
              })}
            </ControlCopy>
            {detail.autoMatch ? (
              <ControlCopy>
                {t("settings.dataManagement.autoApplyWarning")}
              </ControlCopy>
            ) : null}
          </>
        ) : detail.kind === "uaePass" ? (
          <UaePassPanel
            embedded
            onLink={() =>
              surface.leave(() => {
                void openUaePassFlow(router, {
                  mode: "settings_link",
                  dismissCurrentSurface: Platform.OS === "ios",
                });
              })
            }
          />
        ) : detail.kind === "connections" ? (
          <>
            <ControlCopy>
              {t("settings.dataManagement.connectionsDescription")}
            </ControlCopy>
            <ControlSection
              title={t("settings.dataManagement.connectionsTitle")}
            >
              <ControlRow
                title={t("nav.connectors")}
                onPress={() => leave("/(app)/connectors")}
              />
              <ControlRow
                title={t("settings.mcpAccess.title")}
                onPress={() => leave("/settings/mcp")}
              />
              {isSettingsCategoryAvailable("connectedApps") ? (
                <ControlRow
                  title={t("settings.connectedApps.title")}
                  onPress={() => leave("/settings/connected-apps")}
                />
              ) : null}
            </ControlSection>
          </>
        ) : (
          <>
            <ControlCopy>
              {t("settings.dataManagement.clearMemoryBody")}
            </ControlCopy>
            <ControlRow
              title={t("settings.aiMemory")}
              description={t("settings.controls.reviewMemory")}
              onPress={() => leave("/settings/memory")}
            />
          </>
        )}
      </SettingsDetailSurface>
      <DataManagementMainCvSheet
        visible={mainCvOpen}
        onClose={() => setMainCvOpen(false)}
      />
      {!onDeleteAccount ? (
        <SettingsAccountDeletion
          visible={deletingAccount}
          onClose={() => setDeletingAccount(false)}
        />
      ) : null}
    </>
  );
}
